import * as React from "react";
import Box from "@mui/material/Box";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { IAppUser } from "src/interface/IUser";
import { EditToolbarRelatedUser } from "./EditToolbarRelatedUser";
import { Stack } from "@mui/material";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

interface relatedUserProps {
  userRef?: string;
  childsUser?: IAppUser[];
  deletedUserAsync: UseMutateAsyncFunction<any, Error, any, unknown>;
  deleteLoading: boolean;
  insertAsync: UseMutateAsyncFunction<any, Error, any, unknown>;
  insertLoading: boolean;
  updateAsync: UseMutateAsyncFunction<any, Error, any, unknown>;
  updateLoading: boolean;
  userData?: IAppUser;
  onClose: () => void;
}

export default function RelatedUser({
  userRef,

  childsUser,
  deletedUserAsync,
  insertAsync,
  updateAsync,
  deleteLoading,
  insertLoading,
  updateLoading,
  userData,
  onClose,
}: relatedUserProps) {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );
  const [rows, setRows] = React.useState<IAppUser[]>([]);

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows?.filter((row: any) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows?.filter((row: any) => row.id !== id)); // حذف ردیف جدید در صورت انصراف
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false }; // علامت‌گذاری ردیف به عنوان "نه جدید"

    //@ts-ignore
    setRows((oldRows: IAppUser[]) =>
      oldRows?.map((row: IAppUser) =>
        row.id === newRow.id ? (updatedRow as IAppUser) : row
      )
    );

    return updatedRow;
  };
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: "roleTitle",
      headerName: "سمت",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "fName",
      headerName: "نام",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "lName",
      headerName: "نام خانوادگی",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },

    {
      field: "phoneNumber",
      headerName: "موبایل",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "telNumber",
      headerName: "تلفن ثابت",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },

    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="انصراف"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="ویرایش"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="حذف"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  React.useEffect(() => {
    if (userRef && userData && userData.childs && userData.childs.length > 0) {
      setRows(userData.childs);
    }
  }, [userRef, userData]);

  return (
    <Box
      sx={{
        height: "auto",
        width: "100%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: (props) => (
            <EditToolbarRelatedUser
              onClose={onClose}
              userRef={userRef}
              updatingRelatedUser={rows}
              setUpdatingRelatedUser={setRows}
              existingRelatedUser={childsUser}
              deletedUserAsync={deletedUserAsync}
              insertAsync={insertAsync}
              updateAsync={updateAsync}
              deleteLoading={deleteLoading}
              insertLoading={insertLoading}
              updateLoading={updateLoading}
              userData={userData}
              rows={props.rows}
              setRowModesModel={props.setRowModesModel}
              setRows={props.setRows}
            />
          ),
        }}
        slotProps={{
          //@ts-ignore
          toolbar: { setRows, setRowModesModel, rows },
        }}
      />
    </Box>
  );
}
